/**
 * JHipster-style authority checking hooks for React frontend.
 * Provides role-based access control matching the backend implementation.
 */

import { useAuth } from '../contexts/AuthContext';

// JHipster Authority Constants (matching backend)
export const AuthorityConstants = {
  ADMIN: 'ROLE_ADMIN',
  USER: 'ROLE_USER',
  USER_ADMIN: 'ROLE_USER_ADMIN',
  USER_ADMIN_D: 'ROLE_USER_ADMIN_D',
  USER_ALL: 'ROLE_USER_ALL',
  USER_MRF: 'ROLE_USER_MRF',
  ANONYMOUS: 'ROLE_ANONYMOUS'
} as const;

export type Authority = typeof AuthorityConstants[keyof typeof AuthorityConstants];

/**
 * Hook for checking user authorities (JHipster pattern)
 */
export const useAuthorities = () => {
  const { user } = useAuth();

  /**
   * Check if user has specific authority
   */
  const hasAuthority = (authority: Authority): boolean => {
    if (!user) return false;
    
    // Check if user is superuser (bypass all checks)
    if (user.is_superuser) return true;
    
    // Check user's authorities array (JHipster style)
    if (user.authorities && user.authorities.length > 0) {
      return user.authorities.includes(authority);
    }
    
    // Fallback to single role field for backward compatibility
    return user.role === authority;
  };

  /**
   * Check if user has any of the specified authorities
   */
  const hasAnyAuthority = (authorities: Authority[]): boolean => {
    if (!user) return false;
    
    // Check if user is superuser (bypass all checks)
    if (user.is_superuser) return true;
    
    // Check user's authorities array (JHipster style)
    if (user.authorities && user.authorities.length > 0) {
      return user.authorities.some(userAuth => authorities.includes(userAuth as Authority));
    }
    
    // Fallback to single role field for backward compatibility
    return authorities.includes(user.role as Authority);
  };


  // JHipster-style permission checks (matching backend User model methods)
  
  const canSeeAllPersons = (): boolean => {
    return hasAnyAuthority([
      AuthorityConstants.ADMIN,
      AuthorityConstants.USER_ADMIN,
      AuthorityConstants.USER_ADMIN_D,
      AuthorityConstants.USER_ALL
    ]);
  };

  // ROLE_USER_ALL can view Private + Public but NOT Inactive profiles.
  // Inactive visibility is limited to the create/edit-capable admin tiers.
  const canSeeInactivePersons = (): boolean => {
    return hasAnyAuthority([
      AuthorityConstants.ADMIN,
      AuthorityConstants.USER_ADMIN,
      AuthorityConstants.USER_ADMIN_D
    ]);
  };

  const canCreatePerson = (): boolean => {
    return hasAnyAuthority([
      AuthorityConstants.ADMIN,
      AuthorityConstants.USER_ADMIN,
      AuthorityConstants.USER_ADMIN_D
    ]);
  };

  const canEditPerson = (): boolean => {
    return hasAnyAuthority([
      AuthorityConstants.ADMIN,
      AuthorityConstants.USER_ADMIN,
      AuthorityConstants.USER_ADMIN_D
    ]);
  };

  const canDeletePerson = (): boolean => {
    return hasAnyAuthority([
      AuthorityConstants.ADMIN,
      AuthorityConstants.USER_ADMIN_D
    ]);
  };

  const canManagePhotos = (): boolean => {
    return hasAnyAuthority([
      AuthorityConstants.ADMIN,
      AuthorityConstants.USER_ADMIN,
      AuthorityConstants.USER_ADMIN_D
    ]);
  };

  const canDeletePhotos = (): boolean => {
    return hasAnyAuthority([
      AuthorityConstants.ADMIN,
      AuthorityConstants.USER_ADMIN_D
    ]);
  };

  const canManageBiodata = (): boolean => {
    return hasAnyAuthority([
      AuthorityConstants.ADMIN,
      AuthorityConstants.USER_ADMIN,
      AuthorityConstants.USER_ADMIN_D
    ]);
  };

  const canDeleteBiodata = (): boolean => {
    return hasAnyAuthority([
      AuthorityConstants.ADMIN,
      AuthorityConstants.USER_ADMIN_D
    ]);
  };

  // MRF visibility is intentionally narrower than person/photo/biodata.
  // ROLE_USER_ADMIN_D needs to see the section to exercise delete rights;
  // ROLE_USER_MRF is the dedicated manager role. ROLE_USER_ADMIN and
  // ROLE_USER_ALL deliberately do NOT see MRF.
  const canViewMRF = (): boolean => {
    return hasAnyAuthority([
      AuthorityConstants.ADMIN,
      AuthorityConstants.USER_MRF,
      AuthorityConstants.USER_ADMIN_D
    ]);
  };

  const canManageMRF = (): boolean => {
    return hasAnyAuthority([
      AuthorityConstants.ADMIN,
      AuthorityConstants.USER_MRF
    ]);
  };

  const canDeleteMRF = (): boolean => {
    return hasAnyAuthority([
      AuthorityConstants.ADMIN,
      AuthorityConstants.USER_ADMIN_D
    ]);
  };

  const canImportUsers = (): boolean => {
    return hasAnyAuthority([
      AuthorityConstants.ADMIN,
      AuthorityConstants.USER_ADMIN,
      AuthorityConstants.USER_ADMIN_D
    ]);
  };

  // ROLE_USER permissions (Public profiles only)
  const canViewPhotos = (): boolean => {
    return hasAnyAuthority([
      AuthorityConstants.ADMIN,
      AuthorityConstants.USER_ADMIN,
      AuthorityConstants.USER_ADMIN_D,
      AuthorityConstants.USER_ALL,
      AuthorityConstants.USER
    ]);
  };

  const canViewBiodata = (): boolean => {
    return hasAnyAuthority([
      AuthorityConstants.ADMIN,
      AuthorityConstants.USER_ADMIN,
      AuthorityConstants.USER_ADMIN_D,
      AuthorityConstants.USER_ALL,
      AuthorityConstants.USER
    ]);
  };

  const canViewPersons = (): boolean => {
    return hasAnyAuthority([
      AuthorityConstants.ADMIN,
      AuthorityConstants.USER_ADMIN,
      AuthorityConstants.USER_ADMIN_D,
      AuthorityConstants.USER_ALL,
      AuthorityConstants.USER
    ]);
  };

  /**
   * Get user role description for display
   */
  const getRoleDescription = (): string => {
    if (!user) return 'Not authenticated';
    
    switch (user.role) {
      case AuthorityConstants.ADMIN:
        return 'Administrator (Full Access)';
      case AuthorityConstants.USER_ADMIN_D:
        return 'User Admin with Delete (Full Access + Delete)';
      case AuthorityConstants.USER_ADMIN:
        return 'User Admin (Create/Edit Access)';
      case AuthorityConstants.USER_ALL:
        return 'User All (View All Records)';
      case AuthorityConstants.USER_MRF:
        return 'MRF Specialist (Media Release Forms)';
      case AuthorityConstants.USER:
        return 'User (Public Profiles Only)';
      default:
        return 'Unknown Role';
    }
  };

  return {
    // Core authority checking functions
    hasAuthority,
    hasAnyAuthority,
    
    // JHipster-style permission checks
    canSeeAllPersons,
    canSeeInactivePersons,
    canCreatePerson,
    canEditPerson,
    canDeletePerson,
    canManagePhotos,
    canDeletePhotos,
    canManageBiodata,
    canDeleteBiodata,
    canViewMRF,
    canManageMRF,
    canDeleteMRF,
    canImportUsers,
    canViewPhotos,
    canViewBiodata,
    canViewPersons,
    
    // Utility functions
    getRoleDescription,
    
    // Debug helper methods
    getCurrentUserRole: () => user?.role || 'Not authenticated',
    getUserAuthorities: (): string[] => {
      if (!user) return [];
      if (user.is_superuser) return ['SUPERUSER'];
      if (user.authorities && user.authorities.length > 0) {
        return user.authorities;
      }
      // Fallback to single role
      return user.role ? [user.role] : [];
    },
    isSuperuser: () => user?.is_superuser || false,
    
    // Current user info
    user,
    isAuthenticated: !!user
  };
};

